import type { InquiryType, Outcome } from "./types";
import { HOURS } from "./window";

/**
 * The ghost auto-label (Composer 4.5, Master Spec line 231).
 *
 * Verbatim: "no reply 21 days after last outbound (10 for repairs) ->
 * `ghosted` auto, reversed instantly if the customer returns."
 *
 * This is the only auto-label the system can actually write today. The other
 * two 4.5 names are not built and are not faked: `booked` needs the scheduler
 * and `deposit` needs the payment integration, and neither exists, so neither
 * is guessed at from message text.
 *
 * The rule lives here rather than in the sweep's SQL for the reason the
 * `needs-tag` filter gives: a second SQL expression for the same question is a
 * second definition, and the two drift. The sweep narrows the candidate set in
 * SQL using `GHOST_EARLIEST_MS`, which can only over-select, and then asks
 * this function.
 */

/** The default: three weeks of silence after we spoke last. */
export const GHOST_AFTER_MS = 21 * 24 * HOURS;

/**
 * Repairs, which the spec shortens to ten days.
 *
 * A repair quote is a decision measured in days, not weeks: the piece is
 * broken now and the customer either brings it in or takes it elsewhere.
 */
export const GHOST_AFTER_REPAIR_MS = 10 * 24 * HOURS;

/**
 * The shortest threshold any thread can take, and therefore the only safe
 * bound for a prefilter. A query that narrowed on 21 days would never show
 * this function a repair that ghosted at day 10.
 */
export const GHOST_EARLIEST_MS = Math.min(GHOST_AFTER_MS, GHOST_AFTER_REPAIR_MS);

export interface GhostInput {
  readonly lastOutboundAt: Date | null;
  readonly lastCustomerMessageAt: Date | null;
  /**
   * The latest unreversed outcome on the thread, or null if none. An open
   * `ghosted` belongs here too: it is what makes a second sweep a no-op.
   */
  readonly outcome: Outcome | string | null;
  /**
   * Which kind of inquiry this thread is, if anything knows.
   *
   * SPEC GAP (FINDINGS TA-91): no thread carries one. `routeConversation`
   * decides a scenario at compose time and the answer is thrown away with the
   * request, and `inquiry_type` is a column on `outcomes` only, which is the
   * row this label is about to write. An owner-typed `outcomes.inquiry_type`
   * (sheet item 146) sits on an outcome row, and the one sweep that calls this
   * (`sweepGhostLabels`) only reads threads with no standing outcome and selects
   * NULL, so in production this is still always null and the repair arm is
   * unreachable.
   * It is implemented and tested because the rule is the spec's rule, not
   * because a caller supplies it yet.
   */
  readonly inquiryType: InquiryType | string | null;
  readonly now: Date;
}

/** How long this particular thread has to answer before it counts as gone. */
export function ghostThresholdMs(inquiryType: InquiryType | string | null): number {
  // Unknown takes the longer window. Section 0: precision beats recall, and a
  // label written eleven days early is a wrong row in the learning set that
  // nothing downstream will ever question.
  return inquiryType === "repair" ? GHOST_AFTER_REPAIR_MS : GHOST_AFTER_MS;
}

export function isGhosted(input: GhostInput): boolean {
  // A label the owner tapped is the ground truth this whole table exists to
  // hold. The machine never writes over one, and an open `ghosted` row stops
  // the sweep writing a second. `ongoing` is the enum's word for unknown, so
  // it settles nothing, exactly as `needsOutcomeTag` reads it.
  if (input.outcome !== null && input.outcome !== "ongoing") return false;

  // "21 days after last outbound". No outbound, no clock.
  const out = input.lastOutboundAt;
  if (out === null) return false;

  // They answered. Whatever this thread is, it is not a customer who walked
  // away, and calling it one would teach the learner our own silence.
  const back = input.lastCustomerMessageAt;
  if (back !== null && back.getTime() >= out.getTime()) return false;

  const elapsed = input.now.getTime() - out.getTime();
  // Clock skew fails towards silence, like the tag nudge. An auto-label nobody
  // can explain is worse than one that arrives a day late.
  if (elapsed < 0) return false;

  return elapsed >= ghostThresholdMs(input.inquiryType);
}
